import type { ExperienceOfferPageState } from 'contexts/Experience/ExperienceOfferPage/experienceOfferPageStateReducer'
import type GlobalSearchState from 'contexts/GlobalSearch/GlobalSearchState'
import type { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { arrayToMap } from 'lib/array/arrayUtils'
import { findCheapestTourV2Variation } from 'lib/tours/tourUtils'
import {
  isTourV2Offer,
  isTourV2OfferSummary,
  isLEOffer,
  isBedbankOffer,
  isExperienceOffer,
} from 'lib/offer/offerTypes'
import type { Moment } from 'moment'
import moment from 'moment'
import { useIntl } from 'react-intl'
import type { OfferTypeForTripItemTypes } from 'tripPlanner/hooks/api/common'
import type {
  BasicTrip,
  BookmarkIdSet,
  TripItemAndOfferAndDatesIdSet,
} from 'tripPlanner/types/common'
import type { BookmarkDatesAndOccupancy } from 'tripPlanner/types/tripItem'

const includedParams = new Set([
  'originAirportCode',
  'originAirportName',
  'destinationAirportCode',
  'destinationAirportName',
  'departDate',
  'returnDate',
  'adults',
  'children',
  'infants',
  'childrenAge',
  'fareClass',
  'fareType',
])

export type SaveItemsCallbackResult = {
  savedItemIds: Array<string>
}

export function flightSearchQueryStringToBookmarkId(queryString: string) {
  const params = new URLSearchParams(queryString)
  for (const param of [...params.keys()]) {
    if (!includedParams.has(param)) {
      params.delete(param)
    }
  }
  return '?' + params.toString()
}

export function flightSearchQueryStringWithoutTripId(queryString: string) {
  const params = new URLSearchParams(queryString)
  params.delete('tripId')
  return '?' + params.toString()
}

export function flightSearchQueryStringWithoutAirportNames(
  queryString: string,
) {
  const params = new URLSearchParams(queryString)
  params.delete('originAirportName')
  params.delete('destinationAirportName')
  return '?' + params.toString()
}

export const getPropertyName = (offer: OfferTypeForTripItemTypes): string => {
  if ((isLEOffer(offer) || isBedbankOffer(offer)) && offer.property) {
    return offer.property.name
  }

  return offer.name
}

export const getOfferImageId = (
  offer: Exclude<OfferTypeForTripItemTypes, App.CruiseOffer> | App.ExperienceOffer,
  selectedVariation?: App.Tours.TourV2OfferVariation,
): string | undefined => {
  if (isExperienceOffer(offer)) {
    return offer.images?.[0]?.id
  }
  if (isTourV2Offer(offer) || isTourV2OfferSummary(offer)) {
    const variation = selectedVariation ?? findCheapestTourV2Variation(offer)
    return variation?.images[0]?.id
  }

  return offer.image?.id ?? offer.images?.[0]?.id
}

export const getDatesAndGuests = (
  offerPageState: OfferPageState | undefined,
  globalSearchState: GlobalSearchState | undefined,
): BookmarkDatesAndOccupancy => {
  if (offerPageState) {
    const checkInMoment: Moment | undefined = offerPageState.checkInDate ? moment(offerPageState.checkInDate) : undefined
    let checkOutMoment: Moment | undefined = offerPageState.checkOutDate ? moment(offerPageState.checkOutDate) : undefined
    if (!checkOutMoment && (checkInMoment && offerPageState.duration)) {
      checkOutMoment = checkInMoment.clone().add(offerPageState.duration, 'days')
    }

    return {
      checkInDate: checkInMoment,
      checkOutDate: checkOutMoment,
      durationInDays: offerPageState.duration,
      occupancies: offerPageState.rooms,
    }
  }
  if (globalSearchState) {
    return {
      checkInDate: globalSearchState.checkinDate,
      checkOutDate: globalSearchState.checkoutDate,
      occupancies: globalSearchState.occupancies,
    }
  }
  return {}
}

export const needHotelPrice = (
  datesAndGuests: BookmarkDatesAndOccupancy,
): boolean =>
  !!datesAndGuests.checkInDate &&
  !!datesAndGuests.checkOutDate &&
  !!datesAndGuests.occupancies &&
  datesAndGuests.occupancies.length > 0

export type BookmarkLabelVariant = 'default' | 'room' | 'search'

export function useBookmarkLabelText(
  isSaved: boolean,
  variant: BookmarkLabelVariant = 'default',
): string {
  const intl = useIntl()

  if (isSaved) {
    return intl.formatMessage({
      id: '7biD9Bz7UsUUMYqYvKpr',
      defaultMessage: 'Saved',
      description: 'Form field label for saved state in bookmark component',
    })
  }

  if (variant === 'room') {
    return intl.formatMessage({
      id: 'qLNtyOGhs8GstemBbbl7',
      defaultMessage: 'Save room',
      description: 'Form field label for save room button in bookmark component',
    })
  }

  if (variant === 'search') {
    return intl.formatMessage({
      id: 'OIIrhBrS9jPWYtibMeYF',
      defaultMessage: 'Save search',
      description: 'Label for save search button in bookmark component',
    })
  }

  return intl.formatMessage({
    id: 'cJFRzp0axiAfmEMXL4c4',
    defaultMessage: 'Save',
    description: 'Form field label for save button in bookmark component',
  })
}

const plannedIdSetContainsOfferId = (
  offerId: string,
  roomTypeId?: string,
): ((plannedIdSet: TripItemAndOfferAndDatesIdSet) => boolean) => {
  return (plannedIdSet) =>
    plannedIdSet.offerIdAndMeta.startsWith(offerId) &&
    !!(!roomTypeId || roomTypeId === plannedIdSet.roomTypeId)
}

const bookmarkIdSetContainsOfferId =
  (offerId: string): ((bookmarkIdSet: BookmarkIdSet) => boolean) =>
    (bookmarkIdSet) =>
      bookmarkIdSet.offerId === offerId

type SavedDetail = { trip: BasicTrip, itemId: string }

export const getBookmarkDetailsForOffer = (
  trips: Array<BasicTrip>,
  offerId: string,
): SavedDetail | undefined => {
  const trip = trips.find((trip) =>
    trip.bookmarkIdSets.some(bookmarkIdSetContainsOfferId(offerId)),
  )
  if (trip) {
    const idSet = trip.bookmarkIdSets.find(
      bookmarkIdSetContainsOfferId(offerId),
    )

    if (idSet?.itemId) {
      return { trip, itemId: idSet.itemId }
    }
    return undefined
  }
}

export const getSavedDetailsForOffer = (
  trips: Array<BasicTrip>,
  offerId: string,
  roomTypeId?: string,
): Array<SavedDetail> => {
  const foundTrips = trips.flatMap((trip) => {
    const plannedIdSets = trip.plannedIdSets.filter(
      plannedIdSetContainsOfferId(offerId, roomTypeId),
    )
    return plannedIdSets.map((idSet) => ({
      trip,
      itemId: idSet.itemId,
    }))
  })

  if (foundTrips.length > 0) {
    return foundTrips
  } else if (!roomTypeId) {
    const bookmarkDetail = getBookmarkDetailsForOffer(trips, offerId)
    if (bookmarkDetail) {
      return [bookmarkDetail]
    }
  }
  return []
}

function occupanciesHaveChildrenAgeDefined(
  occupancies: Array<App.Occupants>,
): occupancies is Array<App.Occupants & { childrenAge: Array<number> }> {
  return occupancies.every((o) => o.childrenAge)
}

export function convertOccupancies(
  occupancies: Array<App.Occupants> | undefined,
):
  | Array<{
    adults: number
    childrenAge: Array<number>
  }>
  | undefined {
  if (
    occupancies &&
    occupancies.length > 0 &&
    occupanciesHaveChildrenAgeDefined(occupancies)
  ) {
    return occupancies.map((o) => ({
      adults: o.adults,
      childrenAge: o.childrenAge,
    }))
  }
  return undefined
}

export function mapExperienceTicketSelections(
  experienceOfferPageState: ExperienceOfferPageState,
  allTicketOptions: Array<App.ExperienceItemTicket>,
):
  | Array<{
    ticketId: string
    fareType: string
    count: number
    name: string
  }>
  | undefined {
  if (!experienceOfferPageState.ticketCounts) {
    return undefined
  }

  const ticketOptionsById = arrayToMap(allTicketOptions, (option) => option.id)

  return Object.entries(experienceOfferPageState.ticketCounts)
    .filter(([ticketId, count]) => count > 0 && ticketOptionsById.has(ticketId))
    .map(([ticketId, count]) => ({
      ticketId,
      fareType: ticketOptionsById.get(ticketId)!.fareType,
      count,
      name: ticketOptionsById.get(ticketId)!.name,
    }))
}
